/**
 * OfflineStatus — app-level connectivity indicator (Batch 14).
 *
 * Renders two plain, unobtrusive status elements driven by useOfflineStatus:
 *   - A small "Offline — changes will sync" pill while the browser is offline,
 *     showing the pending count once mutations start queuing.
 *   - A transient "Synced N changes" toast when the replay queue drains.
 *
 * Mounted in DataLoader (not Layout) because /review is a full-screen route
 * that lives OUTSIDE Layout — DataLoader is the nearest common ancestor of
 * both the review flow and the Layout pages, so mounting here makes the status
 * visible everywhere a signed-in user can queue offline work.
 *
 * Placement: both elements are fixed and centered. The offline pill sits at
 * `bottom-24` — the same safe band the Review TTS-fallback indicator uses,
 * which clears the mobile bottom nav (--bottom-nav-height, 4rem) and stays
 * above the Review session's bottom action bar. The synced toast uses the
 * top-center slot (matching SaveToast) so it never collides with the pill.
 *
 * Styling is deliberately plain (bg-secondary + border tokens, a short opacity
 * fade) — this is a status change, not an earned celebration, so no confetti,
 * no scale/spring entrance.
 */
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CloudOff, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOfflineStatus } from '@/hooks/useOfflineStatus';

/** How long the "Synced N changes" toast stays on screen before fading out. */
const SYNCED_TOAST_MS = 3500;

export function OfflineStatus() {
  const { t } = useTranslation('common');
  const { isOffline, pendingCount, syncedCount, syncedKey } = useOfflineStatus();

  return (
    <>
      {/* Offline banner — plain status pill, not a celebration */}
      <AnimatePresence>
        {isOffline && (
          <motion.div
            role="status"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full px-4 py-2 text-xs font-medium pointer-events-none"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <CloudOff size={14} />
            {pendingCount > 0 ? t('offline.pending', { count: pendingCount }) : t('offline.banner')}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Synced confirmation — transient, a11y-announced like SaveToast */}
      <SyncedToast count={syncedCount} triggerKey={syncedKey} />
    </>
  );
}

interface SyncedToastProps {
  count: number;
  /** Bumps once per flush to re-trigger the toast; 0 = hidden. */
  triggerKey: number;
}

/**
 * SyncedToast — briefly confirms a completed flush, then auto-dismisses. Each
 * flush bumps `triggerKey`, which restarts the visible window so a later sync
 * re-shows the toast. The `count`/`triggerKey` snapshot is latched on show so
 * the message stays stable while it fades out.
 */
function SyncedToast({ count, triggerKey }: SyncedToastProps) {
  const { t } = useTranslation('common');
  const [shown, setShown] = useState<{ count: number; key: number } | null>(null);

  useEffect(() => {
    if (triggerKey <= 0 || count <= 0) return;
    setShown({ count, key: triggerKey });
    const timer = setTimeout(() => setShown(null), SYNCED_TOAST_MS);
    return () => clearTimeout(timer);
  }, [triggerKey, count]);

  return (
    <div className="fixed top-20 left-1/2 z-50 -translate-x-1/2 pointer-events-none">
      <AnimatePresence>
        {shown && (
          <motion.div
            key={shown.key}
            role="status"
            aria-live="polite"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <RefreshCw size={14} />
            {t('offline.synced', { count: shown.count })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
